// Shared constants & dictionaries (French labels)

export const DEFECT_TYPES = {
  infiltration: "Infiltration / Étanchéité",
  plumbing: "Plomberie / Fuite",
  hvac: "CVC / Climatisation",
  electricity: "Électricité",
  fire_safety: "Sécurité incendie / SSI",
  locksmith: "Serrurerie / Accès",
  facade: "Façade",
  roof: "Toiture",
  structure: "Structure",
  finishes: "Finitions / Second œuvre",
  paving: "Revêtements de sol",
  other: "Autre",
};

export const DEFECT_TYPE_COLORS = {
  infiltration: "#EF4444",
  plumbing: "#3B82F6",
  hvac: "#F59E0B",
  electricity: "#8B5CF6",
  fire_safety: "#DC2626",
  locksmith: "#10B981",
  facade: "#0EA5E9",
  roof: "#7F1D1D",
  structure: "#7F1D1D",
  finishes: "#EAB308",
  paving: "#A16207",
  other: "#6B7280",
};

export const ASSET_TYPES = {
  logistics: "Logistique",
  light_industrial: "Locaux d'Activités",
  industrial: "Industriel",
  retail: "Commerces",
  office: "Bureaux",
  residential: "Résidentiel",
  mixed: "Mixte",
  other: "Autre",
};

export const SEVERITY = {
  minor: { label: "Mineur", color: "bg-slate-100 text-slate-700 border-slate-200" },
  major: { label: "Majeur", color: "bg-amber-100 text-amber-800 border-amber-200" },
  critical: { label: "Critique", color: "bg-red-100 text-red-800 border-red-200" },
};

export const DEFECT_STATUS = {
  open: { label: "Ouvert", color: "bg-slate-100 text-slate-700" },
  qualified: { label: "Qualifié", color: "bg-blue-100 text-blue-700" },
  assigned: { label: "Assigné", color: "bg-indigo-100 text-indigo-700" },
  accepted: { label: "Accepté", color: "bg-cyan-100 text-cyan-700" },
  in_progress: { label: "En cours", color: "bg-amber-100 text-amber-800" },
  follow_up: { label: "Relance", color: "bg-orange-100 text-orange-800" },
  blocked: { label: "Bloqué", color: "bg-red-100 text-red-700" },
  completed: { label: "Réalisé", color: "bg-emerald-100 text-emerald-700" },
  validated: { label: "Validé PM", color: "bg-green-100 text-green-800" },
  rejected: { label: "Rejeté", color: "bg-red-100 text-red-700" },
  closed: { label: "Clôturé", color: "bg-gray-200 text-gray-700" },
  litigation: { label: "Contentieux", color: "bg-rose-100 text-rose-800" },
};

export const KANBAN_COLUMNS = ["open", "qualified", "assigned", "in_progress", "completed", "closed"];

export const CLAIM_FAMILIES = {
  DO: { label: "Dommages-Ouvrage", color: "bg-rose-100 text-rose-800 border-rose-200" },
  multirisk: { label: "Multirisque", color: "bg-blue-100 text-blue-800 border-blue-200" },
};

export const CLAIM_STATUS = {
  draft: "Brouillon", declared: "Déclaré", instruction: "Instruction",
  expert_appointed: "Expert nommé", expertise_scheduled: "Expertise planifiée",
  awaiting_report: "Rapport en attente", position_received: "Position reçue",
  compensation_proposed: "Indemnisation proposée", challenged: "Contesté",
  pre_litigation: "Pré-contentieux", litigation: "Contentieux", closed: "Clôturé",
  under_review: "En instruction", awaiting_documents: "Documents en attente",
  compensation_pending: "Indemnisation en cours", compensated: "Indemnisé", rejected: "Rejeté",
};

export const WARRANTY_TYPES = {
  GPA: "Garantie de Parfait Achèvement",
  GBF: "Garantie Bon Fonctionnement",
  decennial: "Garantie Décennale",
  DO: "Dommages-Ouvrage",
};

// Includes the "none" family for defects hors garantie — used by the tabs/filters/plan colors.
export const WARRANTY_FAMILIES = {
  GPA: { label: "GPA", full: "Garantie de Parfait Achèvement", color: "#3B82F6" },
  GBF: { label: "GBF", full: "Garantie Bon Fonctionnement", color: "#8B5CF6" },
  decennial: { label: "Décennale", full: "Garantie Décennale", color: "#F97316" },
  DO: { label: "DO", full: "Dommages-Ouvrage", color: "#DC2626" },
  none: { label: "Hors garantie", full: "Hors garantie", color: "#6B7280" },
};

export const WARRANTY_FAMILY_KEYS = ["GPA", "GBF", "decennial", "DO", "none"];

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  org_admin: "Admin Organisation",
  property_manager: "Property Manager",
  asset_manager: "Asset Manager",
  technical_manager: "Technical Manager",
  accountant: "Comptable",
  tenant: "Locataire",
  contractor: "Entreprise",
  insurer: "Assureur",
  broker: "Courtier",
  expert: "Expert",
  read_only: "Lecture seule",
};

// Mirrors backend/help_content.py ROLE_TO_PROFILE — used to deep-link "Besoin d'aide ?" buttons.
export const ROLE_TO_HELP_PROFILE = {
  super_admin: "managers", org_admin: "managers", property_manager: "managers",
  asset_manager: "managers", technical_manager: "managers",
  insurer: "external_providers", broker: "external_providers", expert: "external_providers",
  contractor: "external_providers",
  tenant: "tenant",
  accountant: "finance", auditor: "finance", read_only: "finance",
};

// Per-page contextual help target article slug, by help profile. A page not listed for the
// user's profile still links to the Centre d'aide, just without a specific article pre-opened.
export const PAGE_HELP_SLUGS = {
  assets: { managers: "managers-create-asset" },
  claims: { managers: "managers-claims-do", external_providers: "external-claims-view" },
  insurance: { managers: "managers-certificates-attachments" },
};

export const formatEUR = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

export const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
};

export const daysUntil = (iso) => {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
};
